import React, { createContext, useContext, useEffect, useRef } from 'react';
import { Code } from '../../../util/types';
import { ObservationAttachment } from '../AttachmentArbiter/Attachment';

type RenderedObservationKey = string & { readonly __brand: unique symbol };

class RenderedObservation {
  constructor(
    public code: Code,
    public id: string,
    public summaryType: string,
  ) {}

  get key() {
    return `${this.code.key}|${this.code.system}|${this.summaryType}` as RenderedObservationKey;
  }

  equals(other: RenderedObservation) {
    return this.key === other.key && this.id === other.id;
  }
}

class RenderedObservations {
  constructor(
    private observations: Map<
      RenderedObservationKey,
      RenderedObservation
    > = new Map(),
  ) {}

  private getObservation(
    observation: RenderedObservation,
  ): RenderedObservation {
    const existingObservation = this.observations.get(observation.key);
    if (!existingObservation) {
      this.observations.set(observation.key, observation);
      return observation;
    }
    return existingObservation;
  }

  private shouldRender(observation: RenderedObservation): boolean {
    const existingObservation = this.getObservation(observation);
    return existingObservation.equals(observation);
  }

  shouldRenderAttachment(
    observationAttachment: ObservationAttachment,
    summaryType: string,
  ): boolean {
    if (!observationAttachment.primaryCode) {
      return false;
    }
    const observation = new RenderedObservation(
      observationAttachment.primaryCode,
      observationAttachment.identifier,
      summaryType,
    );
    return this.shouldRender(observation);
  }
}

class DefaultRenderedObservations extends RenderedObservations {
  shouldRenderAttachment(
    _observationAttachment: ObservationAttachment,
  ): boolean {
    return true;
  }
}

type ObservationRenderingTrackingContextType = {
  renderedObservations: RenderedObservations;
};

const ObservationRenderingTrackingContext =
  createContext<ObservationRenderingTrackingContextType>({
    renderedObservations: new DefaultRenderedObservations(),
  });

export const ObservationRenderingTrackingProvider = ({
  children,
  context,
}: {
  children: React.ReactNode;
  context: string;
}) => {
  const renderedObservations = useRef<RenderedObservations>(
    new RenderedObservations(),
  );
  const prevContextRef = useRef(context);

  useEffect(() => {
    if (prevContextRef.current !== context) {
      renderedObservations.current = new RenderedObservations();
      prevContextRef.current = context;
    }
  }, [context]);

  return (
    <ObservationRenderingTrackingContext.Provider
      value={{
        renderedObservations: renderedObservations.current,
      }}
    >
      {children}
    </ObservationRenderingTrackingContext.Provider>
  );
};

export const useObservationRenderingTracking = () => {
  return useContext(ObservationRenderingTrackingContext);
};
